<template>
    <DatatablePageLayout>
        <template v-slot:additional>
            <div class="vx-card p-8 w-full mb-6">
                <h4 class="mb-6">{{ $t('admin.photos.filters') }}</h4>
                <div class="flex flex-row gap-4 mb-4">
                    <vs-input
                        class="w-1/4"
                        :label="$t('apps.registered-users.username')"
                        v-model="filters.username"
                    />
                </div>
                <div class="flex flex-row gap-4 mb-4">
                    <vs-input
                        class="w-1/4"
                        :label="$t('admin.tracks.track') + ' Id'"
                        v-model="filters.id"
                    />
                    <vs-input
                        class="w-1/4"
                        :label="$t('admin.tracks.track') + ' Uuid'"
                        v-model="filters.uuid"
                    />
                </div>
                <div class="flex flex-row items-end gap-4 mb-4">
                    <DatePicker
                        class="w-1/4"
                        :label="$t('admin.photos.start_date')"
                        v-model="startDateObject"
                    />
                    <DatePicker
                        class="w-1/4"
                        :label="$t('admin.photos.end_date')"
                        v-model="endDateObject"
                        :min-date="startDateObject ? startDateObject : null"
                    />
                    <vs-button v-if="dateFilter" type="border" class="rounded-lg text-primary" @click="dateFilter = null">
                        {{ $t('admin.photos.reset') }}
                    </vs-button>
                </div>
                <div class="flex items-center gap-4 mt-8">
                    <vs-button class="rounded-lg xs:w-full sm:w-auto" @click="handleFilter">
                        {{ $t('admin.photos.filter') }}
                    </vs-button>
                    <vs-button type="border" class="rounded-lg xs:w-full sm:w-auto text-primary" @click="handleReset">
                        {{ $t('admin.photos.reset') }}
                    </vs-button>
                </div>
            </div>
        </template>
        <template v-slot:datatable>
            <DataTable
                :config="datatableConfig"
                :elements="tracks"
                :stripe="true"
                :draggable="false"
                :sst="true"
                @action="handleAction"
                @filter="filter"
                :pagination="pagination"
            />
        </template>
    </DatatablePageLayout>
</template>

<script>

import { DateTime } from 'luxon';
import loader from "@/modules/Shared/Mixins/loader";
import PaginationService from "@/modules/Shared/Services/PaginationService";
import DataTable from '@/modules/Shared/Components/table/SKDataTable.vue'
import DatatablePageLayout from "@/modules/Shared/Pages/DatatablePageLayout.vue";
import TrackService from "@/modules/Admin/Services/TrackService";
import { download } from '@/modules/Shared/Helpers/fileHelper';
import DatePicker from "@/modules/Shared/Components/form/DatePicker.vue";

export default {
    name: "TracksPage",
    components: {DataTable, DatatablePageLayout, DatePicker},
    mixins: [loader],
    data() {
        return {
            tracks: [],
            filters: {
                id: '',
                uuid: '',
                username: '',
            },
            dateFilter: null,
            datatableConfig: [
                { 'type': 'image', 'field': 'thumbnail', head: this.$t("tracks.tracks-list.thumbnail"), 'searchable': false, 'sorteable': false },
                { 'type': 'text', 'field': 'id', head: 'id', 'searchable': false, 'sorteable': false },
                { 'type': 'text', 'field': 'short_uuid', head: 'uuid', 'searchable': false, 'sorteable': false },
                { 'type': 'text', 'field': 'time', head: this.$t("tracks.tracks-list.date"), 'searchable': false, 'sorteable': false },
                { 'type': 'text', 'field': 'username', head: this.$t("tracks.tracks-list.user"), 'searchable': false, 'sorteable': false },
                { 'type': 'text', 'field': 'country', head: this.$t("tracks.tracks-list.country"), 'searchable': false, 'sorteable': false },
                { 'type': 'text', 'field': 'resortname', head: this.$t("tracks.tracks-list.resort"), 'searchable': false, 'sorteable': false },
                { 'type': 'text', 'field': 'valid', head: this.$t("admin.tracks.validation.valid"), 'searchable': false, 'sorteable': false },
                { 'type': 'actions', 'field': 'actions', head: this.$t("apps.photos.actions"), 'searchable': false, 'sorteable': false }
            ],
            pagination: {
                per_page: 30,
                total: 0,
            }
        }
    },
    async created() {
        await this.getTracks({ filters: [], page: 1 })
    },
    computed: {
        startDateObject: {
            get() { return this.dateFilter ? DateTime.fromSQL(this.dateFilter.split(';')[0]) : null },
            set(val) {
                const endDate = this.dateFilter ? DateTime.fromSQL(this.dateFilter.split(';')[1]) : val
                const adjustedEndDate = endDate.startOf('day') < val.startOf('day') ? val : endDate
                this.dateFilter = [val.toFormat('yyyy-MM-dd 00:00:00'), adjustedEndDate.toFormat('yyyy-MM-dd 23:59:59')].join(';')
            }
        },
        endDateObject: {
            get() { return this.dateFilter ? DateTime.fromSQL(this.dateFilter.split(';')[1]) : null },
            set(val) {
                const startDate = this.dateFilter ? this.dateFilter.split(';')[0] : val.toFormat('yyyy-MM-dd 00:00:00')
                this.dateFilter = [startDate, val.toFormat('yyyy-MM-dd 23:59:59')].join(';')
            }
        },
    },
    methods: {
        buildFilters() {
            const filters = []
            if (this.filters.id) filters.push({ field: 'id', value: this.filters.id })
            if (this.filters.uuid) filters.push({ field: 'uuid', value: this.filters.uuid })
            if (this.filters.username) filters.push({ field: 'username', value: this.filters.username })
            if (this.dateFilter) filters.push({ field: 'time', value: this.dateFilter })
            return filters
        },
        async getTracks(params) {
            await this.loadAndNotify(async () => {
                let trackResponse = await TrackService.getTracks(PaginationService.parseParameters(params))
                this.tracks = this.parseTracks(trackResponse.data)
                this.pagination = {
                    per_page: trackResponse.per_page,
                    total: trackResponse.total
                }
            }, true)
        },
        parseTracks(tracks) {
            return tracks.map((track) => ({
                ...track,
                short_uuid: track.uuid.slice(0, 5) + '...' + track.uuid.slice(track.uuid.length - 5),
                time: track.time ? DateTime.fromSQL(track.time).toFormat('dd-LL-yyyy HH:mm') : '',
                username: track.user.username,
                country: track.location.country,
                resortname: track.resort ? track.resort.name : '',
                valid: track.is_valid ? '✅' : '❌',
                actions: [ 'edit', 'download' ]
            }))
        },
        handleAction(event, uuid) {
            const track = this.tracks.find(track => track.uuid === uuid)
            switch(event) {
                case 'edit':
                    this.$router.push({name: 'admin-track-validation', params: { uuid: uuid}})
                    break
                case 'download':
                    download(track.kml, track.uuid + '.kml')
                    break
            }
        },
        async handleFilter() {
            await this.getTracks({ filters: this.buildFilters(), page: 1 })
        },
        handleReset() {
            this.filters = { id: '', uuid: '', username: '' }
            this.dateFilter = null
        },
        async filter(params) {
            await this.getTracks({ ...params, filters: this.buildFilters() })
        },
    }
};
</script>
